import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, Timer, Bot, ArrowRight } from 'lucide-react';

const PAYMENT_URL = process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://myeaitradingcompass.com/trading-subscription';

export default function Solution() {
  const features = [
    {
      icon: Shield,
      label: "Risk & R:R",
      description: "Enforces proper risk-reward ratios before every trade",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
      testId: "feature-rr"
    },
    {
      icon: Target,
      label: "SL & TP Clarity",
      description: "Forces stop loss and take profit clarity before execution",
      image: "https://images.unsplash.com/photo-1748439281934-2803c6a3ee36?auto=format&fit=crop&w=800&q=80",
      testId: "feature-sl-tp"
    },
    {
      icon: Timer,
      label: "Slows Impulsive",
      description: "Adds friction to emotional, impulsive trading decisions",
      image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?auto=format&fit=crop&w=800&q=80",
      testId: "feature-impulsive"
    },
    {
      icon: Bot,
      label: "AI Risk Manager",
      description: "Acts as a personal AI risk manager questioning every decision",
      image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=800&q=80",
      testId: "feature-ai-risk"
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-slate-50">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight"
            data-testid="solution-title"
          >
            What if execution was checked BEFORE the trade?
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-4 text-base md:text-lg text-[#162959]"
          >
            Trading Compass sits between analysis and execution
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={feature.testId}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200 group"
              >
                {/* Background Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={feature.image}
                    alt={feature.label}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-white/20 to-transparent" />
                </div>
                
                {/* Content */}
                <div className="p-6 md:p-8 relative -mt-20">
                  <div className="w-12 h-12 rounded-xl bg-[#337AF7]/10 border border-[#337AF7]/20 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-[#337AF7]" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-[#162959] mb-2">
                    {feature.label}
                  </h3>
                  <p className="text-sm md:text-base text-[#162959]/70">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-10 text-center"
        >
          <a
            href={PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="solution-cta-button"
            className="inline-flex items-center rounded-xl bg-[#337AF7] text-white font-semibold px-8 py-4 text-base shadow-lg hover:bg-[#2563eb] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#337AF7]/50 transition-all duration-200"
          >
            Start 21-Day Challenge
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-lg md:text-xl text-[#162959] font-semibold">
            This is not a journal.
          </p>
          <p className="text-lg md:text-xl text-[#337AF7] font-semibold mt-2">
            This is intervention before damage happens.
          </p>
        </motion.div>
      </div>
    </section>
  );
}